"use client";

import React, { useState, useEffect } from "react";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";
import { validateAndSanitizeContactForm, isRateLimited } from "../../utils/security";

// ─── Grid styles ──────────────────────────────────────────────────────────────
const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};
const greenOrangeGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.10) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.10) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const contactSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Fiieve — Start a Project Today",
  "url": "https://fiieve.com/contact-us",
  "description": "Get in touch with Fiieve — a UK-based software and engineering company. Contact us to discuss web development, mobile apps, AI automation, IoT, and embedded systems projects.",
  "publisher": {
    "@type": "Organization",
    "name": "Fiieve",
    "url": "https://fiieve.com",
    "logo": "https://fiieve.com/Images/logo.png",
    "telephone": "+447843735258",
    "email": "info@fiieve.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "15 Kingston Road",
      "addressLocality": "Ilford",
      "addressRegion": "Essex",
      "postalCode": "IG1 1PA",
      "addressCountry": "GB"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "telephone": "+447843735258",
      "email": "info@fiieve.com",
      "availableLanguage": "English",
      "contactOption": "TollFree"
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",       "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Contact Us", "item": "https://fiieve.com/contact-us" }
    ]
  }
});

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.1 } },
};

// ✅ EmailJS credentials from .env
const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function ContactPage() {
  const { isDark, theme } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isGreenOrange = theme === "greenOrange";
  const isLight       = !isDark && !isGreenOrange;

  const sectionBg   = isDark ? "bg-black text-white" : isGreenOrange ? "bg-[#0d1a0e] text-white" : "bg-[#f0f4f0] text-gray-900";
  const sectionGrid = isLight ? lightGridStyle : isGreenOrange ? greenOrangeGridStyle : darkGridStyle;
  const accentText  = isGreenOrange ? "text-green-400" : isDark ? "text-green-400" : "text-green-700";
  const headingText = isDark || isGreenOrange ? "text-white" : "text-gray-900";
  const subText     = isDark || isGreenOrange ? "text-gray-400" : "text-gray-600";
  const cardBg      = isDark ? "bg-gray-900" : "bg-[#1a2e1a]";
  const inputBg     = isDark ? "bg-black"    : "bg-[#0d1a0e]";

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", companyName: "",
    service: "", budget: "", timeline: "", message: "",
  });
  const [errors, setErrors]             = useState({});
  const [submitted, setSubmitted]       = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState("");

  const services = [
    "Web Development",
    "Mobile App Development",
    "UI/UX & Graphic Design",
    "Custom Software Development",
    "AI Automation",
    "IoT Development",
    "Embedded Systems",
    "Robotics & Automation",
    "PCB Design",
    "Smart Home Systems",
  ];

  const requiredFields = ["name", "email", "phone", "service"];

  const validateForm = () => {
    const newErrors = {};
    requiredFields.forEach((f) => {
      if (!formData[f]?.trim()) newErrors[f] = "This field is required";
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Please enter a valid email address";
    if (formData.phone && !/^[\d\s\-+]+$/.test(formData.phone))
      newErrors.phone = "Please enter a valid phone number";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    if (submitError) setSubmitError("");
  };

  const handleService = (s) => {
    setFormData((p) => ({ ...p, service: s }));
    if (errors.service) setErrors((p) => ({ ...p, service: "" }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  // ✅ Rate limiting — prevents spam
  if (isRateLimited("contact", 30000)) {
    setSubmitError("Please wait 30 seconds before submitting again.");
    return;
  }

  // ✅ Validate and sanitize all inputs
  const { errors, isValid, sanitized } = validateAndSanitizeContactForm(formData);
  if (!isValid) {
    setErrors(errors);
    return;
  }

  setIsSubmitting(true);
  setSubmitError("");

  try {
    await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
      from_name:    sanitized.name,
      from_email:   sanitized.email,
      reply_to:     sanitized.email,
      phone:        sanitized.phone,
      company_name: sanitized.companyName || "Not provided",
      service:      sanitized.service,
      budget:       sanitized.budget      || "Not specified",
      timeline:     sanitized.timeline    || "Not specified",
      message:      sanitized.message     || "No message provided",
    }, PUBLIC_KEY);

    setSubmitted(true);
    setFormData({ name: "", email: "", phone: "", companyName: "", service: "", budget: "", timeline: "", message: "" });
    setTimeout(() => setSubmitted(false), 8000);
  } catch (error) {
    setSubmitError("Unable to send your message. Please try again or email us at info@fiieve.com");
  } finally {
    setIsSubmitting(false);
  }
};


  const contactInfo = [
    {
      icon: <FaPhoneAlt className="text-green-400 text-xl" />,
      label: "Phone",
      value: "+44 7843 735258",
      href: "tel:+447843735258",
      ariaLabel: "Call Fiieve on +44 7843 735258",
    },
    {
      icon: <FaEnvelope className="text-green-400 text-xl" />,
      label: "Email",
      value: "info@fiieve.com",
      href: "mailto:info@fiieve.com",
      ariaLabel: "Email Fiieve at info@fiieve.com",
    },
    {
      icon: <FaMapMarkerAlt className="text-green-400 text-xl" />,
      label: "Address",
      value: "15 Kingston Road, Ilford, Essex IG1 1PA, London",
      href: "https://maps.google.com/?q=15+Kingston+Road+Ilford+Essex+IG1+1PA+London",
      ariaLabel: "View Fiieve office on Google Maps",
    },
    {
      icon: <FaWhatsapp className="text-green-400 text-xl" />,
      label: "WhatsApp",
      value: "+44 7843 735258",
      href: "https://wa.me/447843735258?text=Hello%2C%20I%27m%20interested%20in%20your%20services",
      ariaLabel: "Message Fiieve on WhatsApp",
    },
  ];

  return (
    <>
      <SEOHead
        title="Contact Fiieve | Start a Project Today — UK Software Company"
        description="Contact Fiieve to discuss your web development, mobile app, AI automation, IoT, or embedded systems project. UK-based team. We respond within 24 hours."
        canonical="https://fiieve.com/contact-us"
        ogImage="https://fiieve.com/Images/og-contact.jpg"
        schema={contactSchema}
      />

      <motion.main
        initial="hidden"
        animate="show"
        variants={fadeUp}
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${sectionBg}`}
        style={sectionGrid}
      >
        {isLight && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.11) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.09) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"50%", left:"20%", width:"60vw", height:"40vw", background:"radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)", filter:"blur(55px)" }} />
          </div>
        )}
        {isGreenOrange && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.14) 0%, transparent 70%)", filter:"blur(45px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO BANNER ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
                Contact Us
              </h1>
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Contact Us</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── MAIN CONTENT ── */}
          <div className="px-6 md:px-12 lg:px-20 py-10">
            <div className="max-w-5xl mx-auto">

              <motion.div
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="text-center mb-8"
              >
                <p className={`text-sm font-semibold uppercase tracking-widest ${accentText} mb-1`}>
                  Get In Touch
                </p>
                <h2 className={`text-3xl md:text-4xl font-bold ${headingText} mb-2`}>
                  Let's Discuss Your Project
                </h2>
                <p className={`${subText} text-sm md:text-base max-w-xl mx-auto`}>
                  Whether you need a web app, mobile platform, AI automation system, or IoT solution — tell us about your project and we'll get back to you within 24 hours.
                </p>
              </motion.div>

              {/* ── CONTACT FORM ── */}
              <section aria-labelledby="contact-form-heading">
                <h3 id="contact-form-heading" className="sr-only">Project enquiry form</h3>

                <motion.form
                  onSubmit={handleSubmit}
                  variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className={`space-y-6 ${cardBg} border border-green-400/30 rounded-2xl p-6 md:p-8 mb-8`}
                  noValidate
                  aria-label="Contact Fiieve — project enquiry form"
                >
                  {/* Fields grid */}
                  <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { label: "Full Name",     name: "name",        type: "text",  placeholder: "Enter your full name" },
                      { label: "Email Address", name: "email",       type: "email", placeholder: "Enter your email address" },
                      { label: "Phone Number",  name: "phone",       type: "tel",   placeholder: "Enter your phone number" },
                      { label: "Company Name",  name: "companyName", type: "text",  placeholder: "Enter your company name (optional)" },
                      { label: "Budget Range",  name: "budget",      type: "text",  placeholder: "e.g. Under £5k / £5k–£15k / £15k+" },
                      { label: "Timeline",      name: "timeline",    type: "text",  placeholder: "e.g. 4 weeks / 3 months / Flexible" },
                    ].map(({ label, name, type, placeholder }) => (
                      <div key={name}>
                        <label htmlFor={name} className="block text-white text-sm font-semibold mb-2 uppercase tracking-wide">
                          {label}{" "}
                          {requiredFields.includes(name) && <span className="text-red-400">*</span>}
                        </label>
                        <input
                          id={name} type={type} name={name}
                          value={formData[name]} onChange={handleChange}
                          placeholder={placeholder} disabled={isSubmitting}
                          aria-required={requiredFields.includes(name)}
                          aria-describedby={errors[name] ? `${name}-error` : undefined}
                          className={`w-full px-4 py-3 ${inputBg} border rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-green-400 transition-colors text-sm ${
                            errors[name] ? "border-red-500" : "border-gray-700"
                          }`}
                        />
                        {errors[name] && (
                          <p id={`${name}-error`} className="text-red-400 text-xs mt-1" role="alert">{errors[name]}</p>
                        )}
                      </div>
                    ))}
                  </motion.div>

                  {/* Service pills */}
                  <motion.div variants={fadeUp}>
                    <label className="block text-white text-sm font-semibold mb-3 uppercase tracking-wide">
                      Service of Interest <span className="text-red-400">*</span>
                    </label>
                    <div className="flex flex-wrap gap-3" role="group" aria-label="Select a service">
                      {services.map((s) => (
                        <button
                          key={s} type="button" onClick={() => handleService(s)}
                          aria-pressed={formData.service === s}
                          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-300 ${
                            formData.service === s
                              ? "bg-green-400 text-black border-green-400"
                              : "bg-transparent text-gray-300 border-gray-600 hover:border-green-400 hover:text-green-400"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    {errors.service && (
                      <p className="text-red-400 text-xs mt-2" role="alert">{errors.service}</p>
                    )}
                  </motion.div>

                  {/* Message */}
                  <motion.div variants={fadeUp}>
                    <label htmlFor="message" className="block text-white text-sm font-semibold mb-2 uppercase tracking-wide">
                      Project Description
                    </label>
                    <textarea
                      id="message" name="message" value={formData.message} onChange={handleChange}
                      placeholder="Describe your project, goals, target audience, and any specific requirements..."
                      rows={5} disabled={isSubmitting}
                      className={`w-full px-4 py-3 ${inputBg} border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-green-400 transition-colors resize-none text-sm`}
                    />
                  </motion.div>

                  <p className="text-gray-500 text-xs text-center">
                    🔒 We never share your information. We respond within 24 hours.
                  </p>

                  {/* Submit button */}
                  <motion.div variants={fadeUp}>
                    <button
                      type="submit" disabled={isSubmitting}
                      aria-label="Submit project enquiry to Fiieve"
                      className="w-full bg-green-400 text-black font-bold py-4 rounded-xl hover:bg-green-300 transition-all duration-300 text-base disabled:opacity-50 hover:scale-[1.02]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Sending...
                        </span>
                      ) : submitted ? "✓ Message Sent!" : "Send Message"}
                    </button>
                  </motion.div>

                  {/* Success */}
                  {submitted && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      role="status" aria-live="polite"
                      className="bg-green-500/10 border border-green-500/50 text-green-400 px-6 py-4 rounded-xl text-center text-sm"
                    >
                      ✓ Your message has been sent! We'll get back to you within 24 hours.
                    </motion.div>
                  )}

                  {/* Error */}
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      role="alert" aria-live="assertive"
                      className="bg-red-500/10 border border-red-500/50 text-red-400 px-6 py-4 rounded-xl text-center text-sm"
                    >
                      {submitError}
                    </motion.div>
                  )}
                </motion.form>
              </section>

              {/* ── CONTACT INFO CARDS ── */}
              <section aria-labelledby="contact-info-heading">
                <h3 id="contact-info-heading" className={`text-xl font-bold ${headingText} text-center mb-5`}>
                  Get in Touch Directly
                </h3>
                <motion.div
                  variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                  itemScope itemType="https://schema.org/Organization"
                >
                  {contactInfo.map((info) => (
                    <motion.a
                      key={info.label}
                      href={info.href}
                      target={info.label === "Address" || info.label === "WhatsApp" ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      variants={fadeUp}
                      whileHover={{ scale: 1.04, boxShadow: "0 0 25px rgba(74,222,128,0.2)" }}
                      aria-label={info.ariaLabel}
                      className={`${cardBg} border border-green-400/30 hover:border-green-400/70 rounded-2xl p-5 flex flex-col items-center text-center gap-3 transition-all duration-300 group`}
                    >
                      <div className="w-12 h-12 rounded-full bg-green-400/10 border border-green-400/30 flex items-center justify-center group-hover:bg-green-400/20 transition-all duration-300">
                        {info.icon}
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{info.label}</p>
                        <p className="text-white text-sm font-medium leading-snug">{info.value}</p>
                      </div>
                    </motion.a>
                  ))}
                </motion.div>
              </section>

            </div>
          </div>
        </div>
      </motion.main>
    </>
  );
}